import { observable } from "@legendapp/state";
import type * as Ably from "ably";

import { syncedCrudPersist } from "~/libs/legend-state";
import { getOrgChannel } from "~/libs/pubsub.lib";
import { trpc } from "~/libs/trpc.lib";
import { appDataReady$ } from "../app-data-ready";
import type { BoardInvitation } from "./board-invitations.types";

/**
 * Org-scoped store of pending board invitations across every board in the
 * active org. The share dialog filters this map down to a single `boardId`.
 *
 * The CRUD `create` callback is intentionally a no-op: invitations are
 * created via `inviteToBoard` in `board-invitations.actions.ts`, which
 * needs the `(boardId, email)` shape — the synthetic `BoardInvitation`
 * row would never round-trip cleanly through `boardInvitations.create`.
 * The action calls the mutation itself and then writes the returned row
 * into the store; the channel broadcast keeps every other client in sync.
 *
 * The CRUD `delete` callback maps to `boardInvitations.cancel`, which
 * soft-cancels the row server-side and broadcasts a `_deleted: true`
 * tombstone so the pending set drops it on every client.
 */
export const boardInvitations$ = observable(
  syncedCrudPersist<BoardInvitation>({
    persist: {
      name: "board-invitations",
    },
    waitFor: appDataReady$,
    list: async () => {
      return await trpc.boardInvitations.list.query();
    },
    delete: async (input) => {
      if (!input.id) return;
      await trpc.boardInvitations.cancel.mutate({
        invitationId: input.id,
      });
    },
    subscribe: ({ update }) => {
      const channel = getOrgChannel();
      if (!channel) return;

      const handler = (message: Ably.Message) => {
        const items = Array.isArray(message.data)
          ? message.data
          : [message.data];
        if (items.length === 0) return;
        update({
          mode: "merge",
          value: items,
        });
      };

      channel.subscribe("board-invitations", handler);

      return () => {
        channel.unsubscribe("board-invitations", handler);
      };
    },
  }),
);
